import { ApiResponse } from '@/services/api-types';

import { createClientLogger } from '@/lib/logger/client-logger';

// 创建错误日志记录器
const logger = createClientLogger('error-handler');

/**
 * 自定义错误类
 * 用于应用程序中统一处理不同类型的错误
 */
export class AppError extends Error {
    public statusCode: number;
    public isOperational: boolean;
    public details?: Record<string, unknown>;

    /**
     * 创建一个应用错误实例
     * @param message 错误消息
     * @param statusCode HTTP状态码，默认为500
     * @param isOperational 是否为可操作错误（业务错误），默认为true
     * @param details 错误详情
     */
    constructor(
        message: string,
        statusCode: number = 500,
        isOperational: boolean = true,
        details?: Record<string, unknown>,
    ) {
        super(message);

        // 设置错误名称
        this.name = this.constructor.name;

        // 设置HTTP状态码
        this.statusCode = statusCode;

        // 设置是否为可操作错误
        this.isOperational = isOperational;

        // 设置错误详情
        this.details = details;

        // 捕获堆栈跟踪
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor);
        }
    }
}

/**
 * 创建并抛出AppError错误
 * @param message 错误消息
 * @param statusCode HTTP状态码
 * @param details 错误详情
 */
export function throwError(
    message: string,
    statusCode: number = 500,
    details?: Record<string, unknown>,
): never {
    throw new AppError(message, statusCode, true, details);
}

/**
 * 错误类型枚举
 */
export enum ErrorType {
    SERVER_ERROR = 500,
    BAD_REQUEST = 400,
    UNAUTHORIZED = 401,
    FORBIDDEN = 403,
    NOT_FOUND = 404,
    VALIDATION_ERROR = 422,
    CONFLICT = 409,
}

/**
 * 通用错误处理函数
 * @param error 错误对象
 * @param errorHandler 自定义错误处理函数
 */
export function handleError(
    error: Error,
    errorHandler?: (error: Error | AppError) => void,
): void {
    // 如果提供了自定义错误处理函数，使用它
    if (errorHandler) {
        errorHandler(error);
        return;
    }

    // 通用错误处理逻辑
    logger.error('应用程序错误', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        ...(error instanceof AppError && {
            statusCode: error.statusCode,
            isOperational: error.isOperational,
            details: error.details,
        }),
    });
}

/**
 * 处理HTTP响应状态码
 * @param response Fetch API的Response对象
 * @param errorMessagePrefix 错误消息前缀（如"API请求"、"文件上传"）
 * @returns 是否需要中断后续处理
 */
export function handleHttpStatusCode(
    response: Response,
    errorMessagePrefix: string = 'HTTP请求',
): boolean {
    // 检查是否是客户端环境
    const isClient = typeof window !== 'undefined';
    if (!isClient) return false;

    const httpStatus = response.status;

    // 检查HTTP状态码，如果不以2XX开头，跳转到500页面
    if (httpStatus < 200 || httpStatus >= 300) {
        const errorMessage = `${errorMessagePrefix}失败，错误代码 ${httpStatus} ，错误信息 ${response.statusText}`;

        // 设置错误信息到cookie
        document.cookie = `error_message=${encodeURIComponent(errorMessage)}; path=/; max-age=5;`;

        // 跳转到500错误页面
        window.location.href = '/errors/500';

        return true;
    }

    return false;
}

/**
 * 通用的API响应状态码处理函数
 * @param result API响应结果
 * @returns 是否需要中断后续处理
 */
export function handleApiResponseStatusCode<T>(
    result: ApiResponse<T>,
): boolean {
    // 检查是否是客户端环境
    const isClient = typeof window !== 'undefined';
    if (!isClient) return false;

    // 获取statusCode
    const statusCode = result.statusCode;

    // S102: 登录认证过期，按当前方式处理
    if (statusCode === 'S102') {
        logger.error(`检测到S102错误码`, {
            statusCode,
            result,
        });
        // 使用动态导入避免循环依赖
        Promise.all([import('./token-utils'), import('./profile-utils')])
            .then(([tokenUtils, profileUtils]) => {
                tokenUtils.clearAuth();
                profileUtils.clearProfile();
                // 跳转到登录页面
                window.location.href = '/login';
            })
            .catch(err => {
                logger.error('S102 自动登出失败', err);
                window.location.href = '/login';
            });
        return true;
    }

    // S101: 401错误，跳转到401错误页面
    if (statusCode === 'S101') {
        logger.error(`检测到S101错误码`, {
            statusCode,
            result,
        });
        document.cookie = `error_message=${encodeURIComponent(result.message || '未授权访问')}; path=/; max-age=5;`;
        const response = result as ApiResponse<T> & { permissionCode?: string };
        // 保存权限码到cookie，用于权限申请
        if (response.permissionCode) {
            document.cookie = `permission_code=${encodeURIComponent(response.permissionCode)}; path=/; max-age=5;`;
        }

        window.location.href = '/errors/401';
        return true;
    }

    // S100: 403错误，跳转到403错误页面
    if (statusCode === 'S100') {
        logger.error(`检测到S100错误码`, {
            statusCode,
            result,
        });
        // 存储错误信息到sessionSto
        document.cookie = `error_message=${encodeURIComponent(result.message || '禁止访问')}; path=/; max-age=5;`;
        window.location.href = '/errors/403';
        return true;
    }

    return false;
}
